import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2Off, ExternalLink, Link, MoreVertical, RefreshCw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { SocialIcon } from '../icons/SocialIcon';
import { LinkBrandModal } from './LinkBrandModal';
import { SocialProfilePicture } from '../common/SocialProfilePicture';
import { DisconnectAccountModal } from './DisconnectAccountModal';
import { executePlatformConnection, invalidateAccountsCache } from '../../utils/socialConnectionUtils';
import type { SocialPlatform } from '../icons/SocialIcon';
import type { Brand } from '../../types';

interface AccountCardProps {
  id: string;
  name: string;
  username: string;
  platform: string;
  pictureUrl: string;
  followersCount: number;
  isVerified: boolean;
  brandId?: string;
  brandName?: string;
  brandLogo?: string;
  onDisconnect: (id: string) => void;
  onLinkToBrand?: (id: string, brandId: string) => void;
  onUnlinkFromBrand?: (id: string) => void;
  availableBrands?: Brand[];
}

export const AccountCard: React.FC<AccountCardProps> = ({
  id,
  name,
  username,
  platform,
  pictureUrl,
  followersCount,
  isVerified,
  brandId,
  brandName,
  brandLogo,
  onDisconnect,
  onLinkToBrand,
  onUnlinkFromBrand,
  availableBrands = []
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showActions, setShowActions] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  const handleViewProfile = () => {
    const baseUrl = platform === 'linkedin' ? 'https://linkedin.com/company' :
                   platform === 'facebook' ? 'https://facebook.com' :
                   platform === 'twitter' ? 'https://twitter.com' :
                   platform === 'instagram' ? 'https://instagram.com' :
                   platform === 'tiktok' ? 'https://tiktok.com/@' :
                   'https://' + platform + '.com';
    
    window.open(`${baseUrl}/${username || id}`, '_blank');
    setShowActions(false);
  };

  const handleReconnect = async () => {
    setShowActions(false);
    setIsReconnecting(true);
    try {
      await invalidateAccountsCache();
      queryClient.removeQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      await executePlatformConnection(platform as SocialPlatform);
    } catch (error) {
      console.error('Error reconnecting account:', error);
      let errorMessage = 'Failed to reconnect account. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      alert(errorMessage);
    } finally {
      setIsReconnecting(false);
    }
  };

  const openDisconnectModal = () => {
    setShowActions(false);
    setIsDisconnectModalOpen(true);
  };

  const confirmDisconnect = () => {
    onDisconnect(id);
  };

  const handleLinkClick = () => {
    setShowLinkModal(true);
    setShowActions(false);
  };

  const handleUnlinkClick = () => {
    onUnlinkFromBrand?.(id);
    setShowActions(false);
  };

  const truncateUsername = (text: string) => {
    if (text.length > 25) {
      return text.substring(0, 25) + '...';
    }
    return text;
  };

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-6 hover:shadow-xl transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <SocialProfilePicture
              pictureUrl={pictureUrl}
              username={username || name}
              platform={platform as SocialPlatform}
              size="lg"
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
              {name}
            </h3>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">@{truncateUsername(username || name)}</span>
                {isVerified && (
                  <span className="text-blue-500">✓</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-1 rounded-lg hover:text-primary-600 transition-colors"
          >
            <MoreVertical className="w-5 h-5 text-gray-500" />
          </button>
          
          {showActions && (
            <>
              <div 
                className="fixed inset-0 z-10"
                onClick={() => setShowActions(false)}
              />
              <div className="absolute right-0 mt-2 w-48 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
                <div className="py-1">
                  <button
                    onClick={handleViewProfile}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:text-primary-600"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('accounts.viewProfile')}
                  </button>
                  {brandId ? (
                    <button
                      onClick={handleUnlinkClick}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:text-primary-600"
                    >
                      <Link2Off className="w-4 h-4 mr-2" />
                      {t('accounts.unlinkBrand')}
                    </button>
                  ) : (
                    <button
                      onClick={handleLinkClick}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:text-primary-600"
                    >
                      <Link className="w-4 h-4 mr-2" />
                      {t('accounts.linkToBrand')}
                    </button>
                  )}
                  <button
                    onClick={handleReconnect}
                    disabled={isReconnecting}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:text-primary-600 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${isReconnecting ? 'animate-spin' : ''}`} />
                    {isReconnecting ? t('accounts.reconnecting') : t('accounts.reconnect')}
                  </button>
                  <button
                    onClick={openDisconnectModal}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Link2Off className="w-4 h-4 mr-2" />
                    {t('accounts.disconnect')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="pt-4 border-t border-gray-100">
        <h4 className="text-xs font-medium text-gray-500 mb-2">Related brand</h4>
        {brandId && brandName ? (
          <div className="flex items-center gap-2">
            <img
              src={brandLogo}
              alt={brandName}
              className="w-5 h-5 rounded-full"
            />
            <span className="text-sm text-gray-600">{brandName}</span>
          </div>
        ) : (
          <button
            onClick={() => setShowLinkModal(true)}
            className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Link className="w-3 h-3" />
            {t('accounts.linkToBrand')}
          </button>
        )}
      </div>

      <LinkBrandModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        onSelect={async (selectedBrandId) => {
          setIsLinking(true);
          try {
            await onLinkToBrand?.(id, selectedBrandId);
            setShowLinkModal(false);
          } catch (error) {
            // Error handling is done in the parent component
          } finally {
            setIsLinking(false);
          }
        }}
        brands={availableBrands}
        accountName={username || name}
        platform={platform as SocialPlatform}
        isLoading={isLinking}
      />

      <DisconnectAccountModal
        isOpen={isDisconnectModalOpen}
        onClose={() => setIsDisconnectModalOpen(false)}
        onConfirm={confirmDisconnect}
        accountName={name}
        username={username}
        pictureUrl={pictureUrl}
        platformName={platform}
        platformId={platform as SocialPlatform}
      />
    </div>
  );
};